import { State, Selector, Action, StateContext } from '@ngxs/store';
import {
  AddItemToCart,
  LogoutAction,
  RemoveAllItemsFromCart,
  RemoveItemFromCart,
  UpdateItemInCart,
  UpdateProfileAction,
  UpdateSelectedEventMonth, UpdateSelectedMerchantCategory, UpdateSelectedNewsCategory, UpdateSelectedStoreCategory
} from './app.actions';
import { CartItem } from './app.models';
import { BootstrapGrowlService } from './components/growl/bootstrap-growl.service';
import { ToastrService } from 'ngx-toastr';
import { Injectable } from '@angular/core';

export class ApplicationStateModel {
  public profile: any;
  public cart: CartItem[];
  public selectedEventMonth: string;
  public selectedMerchantCategory: string;
  public selectedMerchantSubCategory: string;
  public selectedStoreCategory: number | string;
  public selectedNewsCategory: string;
}

@State<ApplicationStateModel>({
  name: 'app',
  defaults: {
    profile: null,
    cart: [],
    selectedEventMonth: null,
    selectedMerchantCategory: null,
    selectedMerchantSubCategory: null,
    selectedStoreCategory: null,
    selectedNewsCategory: null
  } as ApplicationStateModel
})
@Injectable()
export class ApplicationStateHandlers {

  constructor(/*private toastr: ToastrService*/) {}

  @Selector()
  static profile(state: ApplicationStateModel): any {
    return state.profile;
  }

  @Selector()
  static cart(state: ApplicationStateModel): any {
    return state.cart;
  }

  @Selector()
  static selectedEventMonth(state: ApplicationStateModel): any {
    return state.selectedEventMonth;
  }

  @Selector()
  static selectedMerchantCategory(state: ApplicationStateModel): string {
    return state.selectedMerchantCategory;
  }

  @Selector()
  static selectedMerchantSubCategory(state: ApplicationStateModel): string {
    return state.selectedMerchantSubCategory;
  }

  @Selector()
  static selectedStoreCategory(state: ApplicationStateModel): string {
    return state.selectedStoreCategory as string;
  }

  @Selector()
  static selectedNewsCategory(state: ApplicationStateModel): string {
    return state.selectedNewsCategory;
  }

  /**
   * login
   */
  @Action(UpdateProfileAction)
  login({ getState, patchState }: StateContext<ApplicationStateModel>, { profile }: UpdateProfileAction) {
  //console.log('ApplicationState(): login()', profile);
    patchState({
      profile
    });
  }

  /**
   * logout
   */
  @Action(LogoutAction)
  logout({ getState, patchState }: StateContext<ApplicationStateModel>) {
  //console.log('ApplicationState(): logout()');
    patchState({
      profile: null
    });
  }

  /**
   * add item to cart
   */
  @Action(AddItemToCart)
  addItemToCart({ getState, patchState }: StateContext<ApplicationStateModel>, { item }: AddItemToCart) {
  //console.log('ApplicationState(): addItemToCart()', item, getState());
    let currentCart = getState().cart;
    if (!currentCart) {
      currentCart = [];
    }

    const existingItem = currentCart.find(o => {
      if (o.product.id === item.product.id && o.upc.upc === item.upc.upc) {
        return JSON.stringify(o.extra) === JSON.stringify(item.extra);
      }
      return false;
    });

    if (!!existingItem) {
      // update
      patchState({
        cart: currentCart.map(o => {
          if (o.product.id === item.product.id && o.upc.upc === item.upc.upc &&
              JSON.stringify(o.extra) === JSON.stringify(item.extra)) {

            // update this item with extra stuff
            const updated = Object.assign({}, o);
            updated.quantity += item.quantity;
            return updated;
          }
          return o;
        })
      });
      // this.toastr.success(`${item.product.name}${!!item.upc ? ' - ' + (!!item.upc.attribute_set ? item.upc.attribute_set.attribute_value.value : item.upc.upc) : ''} updated in cart`);
    } else {
      // add
      patchState({
        cart: currentCart.concat(item)
      });
      // this.toastr.success(`${item.product.name}${!!item.upc ? ' - ' + (!!item.upc.attribute_set ? item.upc.attribute_set.attribute_value.value : item.upc.upc) : ''} added to cart`);
    }
    // this.bootstrapGrowlService.addAlert(`${item.product.name} added to cart`, BootstrapAlertType.SUCCESS);
  }

  /**
   * remove item from cart
   */
  @Action(RemoveItemFromCart)
  removeItemFromCart({ getState, patchState }: StateContext<ApplicationStateModel>, { item }: RemoveItemFromCart) {
  //console.log('ApplicationState(): removeItemFromCart()', item);
    let currentCart = getState().cart;
    if (!currentCart) {
      currentCart = [];
    }

    patchState({
      cart: currentCart.filter(o => {
        if (o.product.id === item.product.id && ((!o.upc && !item.upc) || (!!o.upc && !!item.upc && o.upc.upc === item.upc.upc)) &&
            JSON.stringify(o.extra) === JSON.stringify(item.extra)) {
          return false;
        }
        return true;
      })
    });
  }

  /**
   * remove all items from cart
   */
  @Action(RemoveAllItemsFromCart)
  removeAllItemsFromCart({ getState, patchState }: StateContext<ApplicationStateModel>, { }: RemoveAllItemsFromCart) {
  //console.log('ApplicationState(): removeAllItemsFromCart()');
    patchState({
      cart: []
    });
  }

  /**
   * update item in cart
   */
  @Action(UpdateItemInCart)
  updateItemInCart({ getState, patchState }: StateContext<ApplicationStateModel>, { item, quantity }: UpdateItemInCart) {
  //console.log('ApplicationState(): updateItemInCart()', item);
    let currentCart = getState().cart;
    if (!currentCart) {
      currentCart = [];
    }

    const existingItem = currentCart.find(o => {
      if (o.product.id === item.product.id && o.upc.upc === item.upc.upc) {
        return JSON.stringify(o.extra) === JSON.stringify(item.extra);
      }
      return false;
    });

    if (quantity > 0) {
      patchState({
        cart: currentCart.map(o => {
          if (o === existingItem) {
            // update this item
            const newItem = Object.assign({}, item);
            newItem.quantity = quantity;
            return newItem;
          }
          return o;
        })
      });
    } else {
      // quantity 0, so just remove from the cart
      patchState({
        cart: getState().cart.filter(o => o !== existingItem)
      });
    }
  }

  /**
   * update selected event month
   */
  @Action(UpdateSelectedEventMonth)
  updateSelectedEventMonth({ getState, patchState }: StateContext<ApplicationStateModel>, { month }: UpdateSelectedEventMonth) {
  //console.log('ApplicationState(): updateSelectedEventMonth()');
    patchState({
      selectedEventMonth: month
    });
  }

  /**
   * update selected merchant category
   */
  @Action(UpdateSelectedMerchantCategory)
  updateSelectedMerchantCategory({ getState, patchState }: StateContext<ApplicationStateModel>, { categoryName, subCategoryName }: UpdateSelectedMerchantCategory) {
  //console.log('ApplicationState(): updateSelectedMerchantCategory()', categoryName, subCategoryName);
    patchState({
      selectedMerchantCategory: categoryName,
      selectedMerchantSubCategory: subCategoryName
    });
  }

  /**
   * update selected store category
   */
  @Action(UpdateSelectedStoreCategory)
  updateSelectedStoreCategory({ getState, patchState }: StateContext<ApplicationStateModel>, { categoryName }: UpdateSelectedStoreCategory) {
  //console.log('ApplicationState(): updateSelectedStoreCategory()');
    patchState({
      selectedStoreCategory: categoryName
    });
  }

  /**
   * update selected store category
   */
  @Action(UpdateSelectedNewsCategory)
  updateSelectedNewsCategory({ getState, patchState }: StateContext<ApplicationStateModel>, { categoryId }: UpdateSelectedNewsCategory) {
  //console.log('ApplicationState(): updateSelectedNewsCategory()');
    patchState({
      selectedNewsCategory: categoryId
    });
  }
}
